import { supabase } from './supabase';

const normalizeBaseUrl = (baseUrl) => {
  if (!baseUrl) return '';
  const trimmed = baseUrl.trim().replace(/\/$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

const configuredBaseUrl = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL);
const isLocalhost = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname);

export const apiBaseUrl = configuredBaseUrl || (isLocalhost ? 'http://localhost:8000/api' : '/api');
// The admin PIN unlock. The server ends it a day after the PIN, or sooner
// when the sign-in that made it ends, so it's kept across tabs and visits.
export const ADMIN_SESSION_STORAGE_KEY = 'cbat_admin_session';
export const ADMIN_SESSION_INVALID_EVENT = 'cbat-admin-session-invalid';

export const apiUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${apiBaseUrl}${normalizedPath}`;
};

export const getAccessToken = async () => {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || null;
};

// A refused sign-in (401) is refreshed once, shared by every request that
// hit it at the same moment.
let pendingRefresh = null;
const refreshSessionOnce = () => {
  if (!pendingRefresh) {
    pendingRefresh = supabase.auth.refreshSession()
      .catch((error) => ({ data: null, error }))
      .finally(() => { pendingRefresh = null; });
  }
  return pendingRefresh;
};

// The refresh token itself was refused (not a network problem): the saved
// sign-in is dead, so stop sending it.
const isDeadSession = (error) => Boolean(error) && [400, 401, 403].includes(error.status);

export const apiFetch = async (path, options = {}, retried = false) => {
  const token = await getAccessToken();
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (path.startsWith('/admin/') && typeof window !== 'undefined') {
    const adminSession = localStorage.getItem(ADMIN_SESSION_STORAGE_KEY);
    if (adminSession) headers.set('X-Admin-Session', adminSession);
  }

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(apiUrl(path), {
    ...options,
    headers
  });

  // The server refused this device's saved sign-in: refresh it and try once
  // more. If it can't be refreshed, sign out here so the site stops failing
  // quietly with a dead session. (Sign-in requests return 401 for a wrong
  // password, which is not this.)
  if (response.status === 401 && token && !retried && supabase && !path.startsWith('/auth/')) {
    const { data, error } = await refreshSessionOnce();
    const freshToken = data?.session?.access_token;
    if (!error && freshToken && freshToken !== token) {
      return apiFetch(path, options, true);
    }
    if (isDeadSession(error)) {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (signOutError) {
        // Signing out locally clears the stored session even if this fails.
      }
    }
  }

  if (path.startsWith('/admin/') && [401, 403].includes(response.status) && typeof window !== 'undefined') {
    localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
    window.dispatchEvent(new Event(ADMIN_SESSION_INVALID_EVENT));
  }
  return response;
};

export const readApiJson = async (path, options = {}) => {
  const response = await apiFetch(path, options);
  let payload = null;

  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    const message = payload?.detail || payload?.message || 'Request failed.';
    const apiError = new Error(message);
    apiError.status = response.status;
    apiError.payload = payload;
    throw apiError;
  }

  return payload;
};
